import React, { useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Text,
  SecondaryButton,
  Heading,
  Metadata,
} from 'components';
import Spinner from 'components/Spinner/Spinner';
import { useDispatch, useSelector } from 'react-redux';
import { AddProfileIcon } from 'components/Icons';
import AvatarImage from './AvatarImage';
import { useProfileContext } from './ProfileContext';
import { ProfilesLayout, AvatarContainer, AvatarImageContainer } from './Styles';
import { ProfileType, PROFILE_AVATAR_SIZE, PROFILE_AVATAR_XS_SIZE } from './utils';
import * as routerActions from 'router/actions';
import { useOverflowContainerWidth } from 'utils/hooks';

function ProfilesListView() {
  const dispatch = useDispatch();
  const {
    profilesLoading,
    profilesError,
    openReturnLocation,
    selectProfile,
    profiles,
    hasReachedProfileLimit,
    setEditProfile,
  } = useProfileContext();
  const location = useSelector((state: any) => state.router.location);
  const listEditMode = location?.name === 'profiles-edit';
  const { containerRef, containerStyle } = useOverflowContainerWidth<HTMLDivElement>([profiles?.length || 0]);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (profilesError) {
      openReturnLocation();
    }
  }, [profilesError, openReturnLocation]);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const setEditMode = useCallback((mode: boolean) => {
    dispatch(routerActions.replace({
      name: mode ? 'profiles-edit' : 'profiles',
    }));
  }, [dispatch]);

  const editProfile = useCallback((profile: ProfileType) => {
    if (!profile?.id) {
      return;
    }
    setEditProfile(profile);
    dispatch(routerActions.push({
      name: 'profile-edit',
      params: { profileId: profile.id },
    }));
  }, [dispatch, setEditProfile]);

  const createNewProfile = useCallback(() => {
    if (hasReachedProfileLimit) {
      return;
    }
    setEditProfile(undefined);
    dispatch(routerActions.push({
      name: listEditMode ? 'profile-new-edit' : 'profile-new',
    }));
  }, [dispatch, hasReachedProfileLimit, listEditMode, setEditProfile]);

  const handleSelectProfile = useCallback(async (profile: ProfileType) => {
    await selectProfile(profile.id);
    openReturnLocation({ reload: !profile?.current });
  }, [openReturnLocation, selectProfile]);


  if (profilesLoading) {
    return (
      <ProfilesLayout>
        <Box column alignItems="center" justifyContent="center" p="auto">
          <Spinner size={3} />
        </Box>
      </ProfilesLayout>
    );
  }

  if (profilesError) {
    return null;
  }

  return (
    <ProfilesLayout key="list" hideInitially>
      <Metadata title="profiles.title" />

      <Box my="large" alignItems="center">
        <Heading
          id={listEditMode ? 'profiles.editProfilesTitle' : 'profiles.title'}
          innerRef={headingRef}
          tabIndex={-1}
        />
      </Box>

      <Box fullWidth>
        <Box
          mx="auto"
          my="xxxlarge"
          gap="xxxlarge"
          xs-columnGap="large"
          row
          wrap="wrap"
          alignItems="center"
          innerRef={containerRef}
          data-cy="profiles-list"
          {...containerStyle}
        >
          {profiles?.map(profile => (
            <Box key={profile.id} column width={PROFILE_AVATAR_SIZE} xs-width={PROFILE_AVATAR_XS_SIZE}>
              <AvatarImage
                src={profile?.imageUrl}
                name={profile.name || '\u00A0'} // || &nbsp;
                alt=""
                size={PROFILE_AVATAR_SIZE}
                xsSize={PROFILE_AVATAR_XS_SIZE}
                hover
                editIcon={listEditMode}
                onClick={() => {
                  if (listEditMode) {
                    editProfile(profile);
                  } else {
                    void handleSelectProfile(profile);
                  }
                }}
              />
            </Box>
          ))}
          {!hasReachedProfileLimit && (
            <Box column width={PROFILE_AVATAR_SIZE} xs-width={PROFILE_AVATAR_XS_SIZE}>
              <AvatarContainer
                size={PROFILE_AVATAR_SIZE}
                xs-size={PROFILE_AVATAR_XS_SIZE}
                hover
                onClick={createNewProfile}
                role="button"
                tabIndex={0}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    createNewProfile();
                  }
                }}
              >
                <AvatarImageContainer size={PROFILE_AVATAR_SIZE} xs-size={PROFILE_AVATAR_XS_SIZE}>
                  <AddProfileIcon width="100%" height="100%" />
                </AvatarImageContainer>
                <Box mt="large">
                  <Text id="profiles.newProfile" align="center" display="block" fontSize="medium" bold />
                </Box>
              </AvatarContainer>
            </Box>
          )}
        </Box>
      </Box>

      {listEditMode && (
        <Box>
          <SecondaryButton
            variant="white"
            onClick={() => setEditMode(false)}
          >
            <Text id="profiles.done" />
          </SecondaryButton>
        </Box>
      )}

      {!listEditMode && (
        <Box>
          <SecondaryButton
            variant="white"
            onClick={() => setEditMode(true)}
          >
            <Text id="profiles.editProfiles" />
          </SecondaryButton>
        </Box>
      )}
    </ProfilesLayout>
  );
}

export default ProfilesListView;

